import { QueryFactory } from '@lmt/shared-types';
import {
  HL7MappingListDto,
  HL7MessageTypeMappingDto,
  HL7TargetDefinition,
} from '@lmt/shared-types';

import { AutoSizedTableWrapper } from '@lmt/unicorn-shared/app/components/common/AutoSizedTableWrapper';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { HL7MessageTypeNames } from '@lmt/unicorn-shared/data/messageTypeNames';
import React, { FC, useEffect, useState } from 'react';
import {
  useDataGrid,
  DXG,
  RowInsertingEvent,
  RowRemovingEvent,
  RowUpdatingEvent,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { allowOnlySingleNewRow } from '@lmt/unicorn-shared/app/common/devexpress/Grid/grid-helper';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useEventCallback } from '@mui/material';

const nameof = nameofFactory<HL7MessageTypeMappingDto>();

type Row = {
  row: RowData;
};
type RowData = {
  data: HL7TargetDefinition;
  removed: boolean;
  key: number;
};

function isDeleteVisible(row: Row) {
  const data = row.row.data;
  return !data.isDefault && !row.row.removed;
}

export const HL7MessageTypeMappingsTable: FC = () => {
  const i18n = useScopedTranslation('hl7.message_types');
  const modals = useModal();
  const [isSaving, setIsSaving] = useState(false);
  useDataGrid();
  const query = QueryFactory.HL7MessageTypeMappingQuery.useGetAllQuery();
  const { data: messageTypeMappingsQueryData } = query;
  const { data: mappings } = QueryFactory.HL7MappingQuery.useGetAllQuery();

  const [messageTypeMappings, setMessageTypeMappings] = useState<
    HL7MessageTypeMappingDto[]
  >([]);

  useEffect(() => {
    if (messageTypeMappingsQueryData) {
      setMessageTypeMappings(messageTypeMappingsQueryData);
    }
  }, [messageTypeMappingsQueryData]);

  const onRowCreate = useEventCallback(
    (row: RowInsertingEvent<HL7MessageTypeMappingDto, string>) => {
      async function onRowCreate(data: HL7MessageTypeMappingDto) {
        try {
          setIsSaving(true);
          await QueryFactory.HL7MessageTypeMappingClient.create(data);
          await query.refetch();
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          setIsSaving(false);
        }
        return false;
      }

      row.cancel = onRowCreate(row.data);
    },
  );

  const onRowEdit = useEventCallback(
    (row: RowUpdatingEvent<HL7MessageTypeMappingDto, string>) => {
      async function onRowEdit(
        oldData: HL7MessageTypeMappingDto,
        data: Partial<HL7MessageTypeMappingDto>,
      ) {
        const patchData = {
          ...oldData,
          ...data,
        };
        if (patchData.id > 10000000) return false;

        try {
          setIsSaving(true);
          await QueryFactory.HL7MessageTypeMappingClient.patch(
            patchData.id,
            data,
          );
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          setIsSaving(false);
        }
        return false;
      }

      row.cancel = onRowEdit(row.oldData, row.newData);
    },
  );

  const onRowDelete = useEventCallback(
    (row: RowRemovingEvent<HL7MessageTypeMappingDto, string>) => {
      async function onRowDelete(row: HL7MessageTypeMappingDto) {
        const id = row.id;
        try {
          setIsSaving(true);
          await QueryFactory.HL7MessageTypeMappingClient.remove(id);
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        } finally {
          setIsSaving(false);
        }
        return false;
      }
      row.cancel = onRowDelete(row.data);
    },
  );

  return (
    <Loading loading={query.isLoading || isSaving} title={i18n.t('loading')}>
      {query.error ? (
        convertToErrorString(query.error)
      ) : (
        <AutoSizedTableWrapper>
          <DXG.DataGrid
            repaintChangesOnly={true}
            onRowInserting={onRowCreate}
            onRowRemoving={onRowDelete}
            onRowUpdating={onRowEdit}
            onToolbarPreparing={allowOnlySingleNewRow}
            dataSource={messageTypeMappings}
            keyExpr={'id'}
            showBorders={true}
            style={{ width: '100%' }}
            onInitNewRow={(e) => {
              const data = e.data as HL7MessageTypeMappingDto;
              data.enabled = true;
              data.visitCreatingEnabled = false;
              data.id = 10000000 + Math.round(Math.random() * 10000);
            }}
            onEditingStart={(e) => {
              const row = e.data as HL7MessageTypeMappingDto;
              if (e.column?.dataField === nameof('enabled')) {
                const mapping = mappings?.find((m) => m.id === row.mappingId);
                if (mapping?.isDeleted) {
                  e.cancel = true;
                }
              }
              if (row.isDefault) {
                if (e.column?.dataField === nameof('messageType')) {
                  e.cancel = true;
                }
              }
            }}
          >
            <DXG.Paging enabled={false} />
            <DXG.Editing
              mode="batch"
              allowUpdating={true}
              allowAdding={true}
              allowDeleting={true}
              selectTextOnEditStart={true}
              startEditAction="click"
            />
            <DXG.Sorting mode="single" />
            <DXG.Column dataField="id" sortOrder="desc" visible={false} />
            <DXG.Column
              dataField={nameof('messageType')}
              dataType="number"
              caption={i18n.t('columns.message_type')}
            >
              <DXG.RequiredRule />
              <DXG.Lookup
                dataSource={HL7MessageTypeNames}
                displayExpr={nameofFactory<(typeof HL7MessageTypeNames)[0]>()(
                  'description',
                )}
                valueExpr={nameofFactory<(typeof HL7MessageTypeNames)[0]>()(
                  'id',
                )}
              />
              <DXG.CustomRule
                message={i18n.t('validation.duplicate_message_type')}
                validationCallback={(item: any) => {
                  const data = item.data as HL7MessageTypeMappingDto;
                  const mappingRuleForSameMessageType =
                    messageTypeMappings.find(
                      (x) =>
                        x.id !== data.id && x.messageType === data.messageType,
                    );
                  if (mappingRuleForSameMessageType) {
                    return false;
                  }
                  return true;
                }}
              />
            </DXG.Column>
            <DXG.Column
              dataField={nameof('mappingId')}
              caption={i18n.t('columns.mapping')}
              dataType="string"
            >
              <DXG.RequiredRule />
              <DXG.Lookup
                dataSource={mappings}
                displayExpr={nameofFactory<HL7MappingListDto>()('name')}
                valueExpr={nameofFactory<HL7MappingListDto>()('id')}
              />
            </DXG.Column>

            <DXG.Column
              width={100}
              dataField={nameof('enabled')}
              caption={i18n.t('columns.enabled')}
              dataType="boolean"
            />
            <DXG.Column
              width={190}
              dataField={nameof('visitCreatingEnabled')}
              caption={i18n.t('columns.visit_creating_enabled')}
              dataType="boolean"
            />
            <DXG.Column type="buttons">
              <DXG.Button name="delete" visible={isDeleteVisible as any} />
            </DXG.Column>
          </DXG.DataGrid>
        </AutoSizedTableWrapper>
      )}
    </Loading>
  );
};
